// Order shipments that have finished packing, tracked in-memory for the
// duration of the active pack session.
export const packedShipments = new Set();

// Order shipments the packer marked as unpackable (missing/damaged items,
// no totes, etc). A shipment is resolved for batch completion when it is
// either packed or unpackable.
export const unpackableShipments = new Set();

// Within-shipment checklist progress for the active pack session: which
// items and rules were checked off before the shipment was finished.
// Tracked at module level so it survives the order-packing screen
// unmounting between shipments, and rides along in packing_state when
// the batch is parked
const packProgress = new Map();

// The batch the in-memory pack state belongs to - pack progress is
// per-batch, so a different batch must never see another batch's marks
let activeBatchId = null;

// Which batch the in-memory marks/progress currently belong to
export function packStateBatchId() {
  return activeBatchId;
}

export function shipmentPackProgress(orderShipmentId) {
  return packProgress.get(orderShipmentId);
}

export function saveShipmentPackProgress(orderShipmentId, progress) {
  packProgress.set(orderShipmentId, progress);
}

export function removeShipmentPackProgress(orderShipmentId) {
  packProgress.delete(orderShipmentId);
}

// Progress entries for the given shipment ids, for including in a park's
// packing_state
export function packProgressForBatch(shipmentIds) {
  const progress = {};
  packProgress.forEach((value, id) => {
    if (shipmentIds.has(id)) progress[id] = value;
  });
  return progress;
}

// Starts pack state for a batch. Marks only carry over while the batch is
// the same one that created them - a new or different batch starts clean.
export function resetPackStateForBatch(batchId) {
  if (activeBatchId === batchId) return;
  packedShipments.clear();
  unpackableShipments.clear();
  packProgress.clear();
  activeBatchId = batchId;
}

export function clearPackState() {
  packedShipments.clear();
  unpackableShipments.clear();
  packProgress.clear();
  activeBatchId = null;
}

// Seeds pack state from a parked batch's saved packing_state so a
// resuming packer sees the shipments already packed or marked unpackable,
// plus each shipment's in-progress checklist
export function restorePackStateForBatch(batchId, packed, unpackable, progress) {
  packedShipments.clear();
  unpackableShipments.clear();
  packProgress.clear();
  packed.forEach(id => packedShipments.add(id));
  unpackable.forEach(id => unpackableShipments.add(id));
  Object.entries(progress ?? {}).forEach(([id, value]) => {
    packProgress.set(id, {
      checkedItemIds: Array.isArray(value?.checked_item_ids) ? value.checked_item_ids.map(String) : [],
      checkedRuleKeys: Array.isArray(value?.checked_rule_keys) ? value.checked_rule_keys.map(String) : [],
      rulesOverridden: !!value?.rules_overridden,
    });
  });
  activeBatchId = batchId;
}
